import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useAIAdvice } from "../useAIAdvice";
import { useTheme, getColors } from "../ThemeContext";  

const C = {
  bg:"#050b14", panel:"#080f1e", card:"#0b1525", border:"#0f2040", border2:"#162a4a",
  cyan:"#00d4ff", teal:"#00ffcc", amber:"#ffb84d", pink:"#ff4da6", purple:"#a855f7",
  blue:"#3b9eff", red:"#ff3d5a", green:"#00ff88", orange:"#ff7c2a",
  text:"#e2eaf8", muted:"#4a6080", muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }  
const MONO = "'Space Mono', monospace";

function renderAdviceWithIcon(text) {
  if (!text) return null;

  const ICONS = {
    "◆": (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
        <circle cx="7" cy="7" r="6" stroke={C.green} strokeWidth="1.5" fill={`${C.green}22`} />
        <path d="M4.3 7.2L6.2 9.1L9.8 5.3" stroke={C.green} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    "▲": (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
        <path d="M7 1.5L13 12.5H1L7 1.5Z" stroke={C.amber} strokeWidth="1.5" fill={`${C.amber}22`} strokeLinejoin="round" />
        <line x1="7" y1="5" x2="7" y2="8.3" stroke={C.amber} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="7" cy="10.2" r="0.9" fill={C.amber} />
      </svg>
    ),
    "▶": (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
        <path d="M7 1.5L13 12.5H1L7 1.5Z" stroke={C.amber} strokeWidth="1.5" fill={`${C.amber}22`} strokeLinejoin="round" />
        <line x1="7" y1="5" x2="7" y2="8.3" stroke={C.amber} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="7" cy="10.2" r="0.9" fill={C.amber} />
      </svg>
    ),
    "■": (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
        <circle cx="7" cy="7" r="6" stroke={C.red} strokeWidth="1.5" fill={`${C.red}22`} />
        <path d="M4.8 4.8L9.2 9.2M9.2 4.8L4.8 9.2" stroke={C.red} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    "▽": (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
        <circle cx="7" cy="7" r="6" stroke={C.red} strokeWidth="1.5" fill={`${C.red}22`} />
        <path d="M4.8 4.8L9.2 9.2M9.2 4.8L4.8 9.2" stroke={C.red} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  };

  for (const [sym, icon] of Object.entries(ICONS)) {
    if (text.startsWith(sym + " ")) {
      return (
        <span style={{ display: "flex", alignItems: "flex-start", gap: 6, lineHeight: 1.5 }}>
          {icon}
          <span>{text.slice(sym.length + 1)}</span>
        </span>
      );
    }
  }
  return <span>{text}</span>;
}

// helper – compare real values, ignore array identity
function payloadEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  return a.every((p, i) =>
    p.dataKey === b[i].dataKey &&
    p.name === b[i].name &&
    p.value === b[i].value
  );
}

export default function AIChartTooltip({ active, payload, label, fieldKey, contextData = {}, getStatusField }) {
  const { isDark } = useTheme();
  applyTheme(getColors(isDark));
  const { advice, loading, fetchAdvice } = useAIAdvice();
  const prevKey = useRef(null);
  const [mousePos, setMousePos] = useState(null);
  const [stable, setStable] = useState({ active: false, payload: null, label: null });
  const hideTimer = useRef(null);
  const lastActiveRef = useRef(0); // timestamp of last time Recharts said active=true

  useEffect(() => {
    const handler = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  useEffect(() => {
    if (active && payload?.length) {
      lastActiveRef.current = Date.now();
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
      // only update state when values really changed → no extra re-renders
      setStable(prev => {
        if (prev.active && prev.label === label && payloadEqual(prev.payload, payload)) {
          return prev;
        }
        return { active: true, payload, label };
      });
    } else {
      // start hide timer only once
      if (!hideTimer.current) {
        hideTimer.current = setTimeout(() => {
          // safety: if Recharts said active=true very recently, keep showing
          if (Date.now() - lastActiveRef.current < 150) {
            hideTimer.current = null;
            return;
          }
          hideTimer.current = null;
          setStable({ active: false, payload: null, label: null });
        }, 280); // short delay – hides quickly when you leave the point
      }
    }
  }, [active, payload, label]);

  // clear timer only on real unmount
  useEffect(() => () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, []);

  useEffect(() => {
    if (!stable.active || !stable.payload?.length) return;

    const allFields = stable.payload
      .filter(p => p.value != null && typeof p.value === "number")
      .map(p => ({
        field: p.dataKey || p.name || fieldKey || "",
        label: p.name || p.dataKey || fieldKey || "",
        value: Math.round(p.value * 100) / 100,
        unit: p.unit || "",
      }));
    if (typeof getStatusField === "function") {
      const extra = getStatusField(stable.payload);
      if (extra) allFields.push(extra);
    }

    const primaryP = stable.payload.find(p => p.dataKey === fieldKey || p.name === fieldKey) || stable.payload[0];
    const val = typeof primaryP.value === "number" ? primaryP.value : null;
    if (val == null) return;

    const allValsKey = allFields.map(f => `${f.field}:${f.value}`).join("|");
    const key = `${fieldKey || primaryP.dataKey}_${Math.round(val * 10) / 10}_${allValsKey}`;
    if (prevKey.current === key) return;
    prevKey.current = key;

    const context = { ...(contextData || {}) };
    allFields.forEach(f => { context[f.field] = f.value; });
    fetchAdvice(fieldKey || primaryP.dataKey, val, primaryP.unit || "", stable.label, context, allFields);
  }, [stable.active, stable.payload, stable.label, fieldKey, getStatusField]);

  if (!stable.active || !stable.payload?.length || !mousePos) return null;

  const adviceColor = !advice ? C.muted
    : advice.startsWith("■") ? C.red
    : advice.startsWith("▲") || advice.startsWith("▶") ? C.amber
    : advice.startsWith("▽") ? C.red
    : C.green;

  const TOOLTIP_W = 320;
  const TOOLTIP_H = 220;
  const OFFSET = 18;
  const viewW = window.innerWidth;
  const viewH = window.innerHeight;
  const rawLeft = mousePos.x + OFFSET + TOOLTIP_W > viewW
    ? mousePos.x - TOOLTIP_W - OFFSET
    : mousePos.x + OFFSET;
  const rawTop = mousePos.y + OFFSET + TOOLTIP_H > viewH
    ? mousePos.y - TOOLTIP_H - OFFSET
    : mousePos.y + OFFSET;
  const left = Math.min(Math.max(rawLeft, 8), viewW - TOOLTIP_W - 8);
  const top  = Math.min(Math.max(rawTop,  8), viewH - TOOLTIP_H - 8);

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed",
      left,
      top,
      background: C.card,
      border: `1px solid ${adviceColor}44`,
      borderLeft: `4px solid ${adviceColor}`,
      borderRadius: 12,
      padding: "12px 16px",
      fontFamily: MONO,
      fontSize: 11,
      boxShadow: "0 20px 50px rgba(0,0,0,0.7)",
      width: TOOLTIP_W,
      zIndex: 999999,
      pointerEvents: "none",
    }}>
      {stable.label != null && (
        <div style={{ color: C.cyan, fontWeight: 700, marginBottom: 8, fontSize: 12 }}>
          {stable.label}
        </div>
      )}

      {stable.payload.map((p, i) => (
        <div key={i} style={{
          display: "flex", justifyContent: "space-between",
          gap: 16, marginBottom: 6, color: C.muted,
        }}>
          <span style={{ fontSize: 10 }}>{p.name || p.dataKey}</span>
          <span style={{ color: p.color || C.cyan, fontWeight: 700, fontSize: 12 }}>
            {typeof p.value === "number" ? p.value.toFixed(2) : p.value}
            {p.unit && <span style={{ color: C.muted, fontSize: 9, marginLeft: 4 }}>{p.unit}</span>}
          </span>
        </div>
      ))}

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 10 }}>
        {loading ? (
          <div style={{ color: C.muted, fontSize: 10 }}>AI Analyzing...</div>
        ) : advice ? (
          <div style={{ color: adviceColor, fontSize: 10, lineHeight: 1.5 }}>{renderAdviceWithIcon(advice)}</div>
        ) : (
          <div style={{ color: C.muted2, fontSize: 10 }}>Hover to get AI recommendation…</div>
        )}
      </div>
    </div>,
    document.body
  );
}